/**
 * `useScrollTrack` — bidirectional "scroll-following" sidebar hook
 * (Twitter / Facebook pattern). The rail follows scroll in BOTH
 * directions instead of pinning to a single edge:
 *
 *   - User scrolls DOWN: rail's bottom eventually meets the viewport
 *     bottom and stays there (clamped at `viewportHeight - innerHeight`).
 *   - User starts scrolling UP from that clamp: the rail immediately
 *     starts moving DOWN with the page (revealing its top), instead of
 *     staying stuck at the bottom until the natural top reappears.
 *   - User keeps scrolling UP: rail's top eventually meets the Topbar
 *     bottom and stays there (clamped at `topOffset`).
 *   - User reverses to DOWN again: rail moves up again until the bottom
 *     clamp.
 *
 * For SHORT rails (shorter than the viewport) the rail just sticks to
 * the top once it scrolls into the pin zone — there's nothing to track
 * below.
 *
 * Disabled entirely when the rail is the tallest column in its row
 * (would leave the main column with nothing to scroll).
 *
 * The hook applies `top` directly via `inner.style.top` on each frame
 * (rAF-throttled) so smooth tracking doesn't re-render React.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

interface Geom {
  left: number;
  width: number;
}

export interface UseScrollTrackOptions {
  /** Top offset (px) when the rail's top clamps to the viewport top. */
  topOffset?: number;
  /**
   * When `false`, the hook short-circuits and never fixes the rail —
   * `tracking` stays `false` and `style` stays `undefined`. Use this when the
   * rail is stacked BELOW the main content (e.g., narrow viewports where the
   * column grid collapses): sticking it to the viewport would just float
   * over the page content the user is trying to read. Defaults to `true`.
   */
  enabled?: boolean;
}

export interface UseScrollTrackResult {
  slotRef: React.RefObject<HTMLElement>;
  innerRef: React.RefObject<HTMLDivElement>;
  /** True when the rail is currently `position: fixed`. */
  tracking: boolean;
  /** Inline style for the inner div (spread it on). */
  style: React.CSSProperties | undefined;
}

export function useScrollTrack(
  options: UseScrollTrackOptions = {},
): UseScrollTrackResult {
  const { topOffset = 64, enabled = true } = options;

  const slotRef = useRef<HTMLElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [geom, setGeom] = useState<Geom | null>(null);
  const [tracking, setTracking] = useState(false);

  // Refs so the scroll handler can read/write without re-subscribing.
  const trackingRef = useRef(false);
  const currentTopRef = useRef(topOffset);
  const lastScrollYRef = useRef(0);

  // Capture the slot's left/width once + on resize so the fixed positioning
  // stays in the same column as the natural slot.
  useLayoutEffect(() => {
    function measure() {
      const slot = slotRef.current;
      if (!slot) return;
      const r = slot.getBoundingClientRect();
      setGeom({ left: r.left, width: r.width });
    }
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  useEffect(() => {
    // Disabled mode: forcibly drop any tracking state from a previous
    // enabled phase (e.g. user resized down past the breakpoint) and skip
    // the scroll/resize listeners entirely.
    if (!enabled) {
      if (trackingRef.current) {
        trackingRef.current = false;
        setTracking(false);
      }
      return undefined;
    }
    let raf = 0;
    function onScroll() {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const slot = slotRef.current;
        const inner = innerRef.current;
        if (!slot || !inner) return;

        const innerHeight = inner.offsetHeight;
        const viewportHeight = window.innerHeight;
        const flexHeight = slot.parentElement?.offsetHeight ?? 0;

        // Skip tracking when the rail is the tallest column — pinning here
        // would leave the main column with no scrollable runway.
        if (flexHeight <= innerHeight + 16) {
          if (trackingRef.current) {
            trackingRef.current = false;
            setTracking(false);
          }
          return;
        }

        const scrollY = window.scrollY;
        const slotTop = slot.getBoundingClientRect().top;

        // Natural-flow region — slot's top hasn't reached the Topbar yet.
        // Leave the rail in flow; reset anchors so the first tracking step
        // starts fresh.
        if (slotTop > topOffset) {
          if (trackingRef.current) {
            trackingRef.current = false;
            setTracking(false);
          }
          currentTopRef.current = topOffset;
          lastScrollYRef.current = scrollY;
          return;
        }

        // First tracking frame — switch to fixed and anchor at topOffset.
        if (!trackingRef.current) {
          trackingRef.current = true;
          setTracking(true);
          currentTopRef.current = topOffset;
          lastScrollYRef.current = scrollY;
          inner.style.top = `${topOffset}px`;
          return;
        }

        const tallerThanViewport = innerHeight > viewportHeight;
        // For tall rails: rail's `top` ranges between
        // `viewportHeight - innerHeight` (bottom clamp) and `topOffset`
        // (top clamp). For short rails the range is degenerate — rail
        // just sticks to the top.
        const minTop = tallerThanViewport ? viewportHeight - innerHeight : topOffset;
        const maxTop = topOffset;

        const dy = scrollY - lastScrollYRef.current;
        lastScrollYRef.current = scrollY;

        // Move rail OPPOSITE to scroll direction (page scrolls down → rail
        // top decreases; page scrolls up → rail top increases). Clamp to
        // [minTop, maxTop] so we stick at the bottom / top edges.
        let next = currentTopRef.current - dy;
        if (next < minTop) next = minTop;
        if (next > maxTop) next = maxTop;
        currentTopRef.current = next;

        // Direct DOM write — no React re-render on every scroll frame.
        inner.style.top = `${next}px`;
      });
    }
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [topOffset, enabled]);

  const style: React.CSSProperties | undefined =
    tracking && geom
      ? {
          position: 'fixed',
          left: geom.left,
          width: geom.width,
          top: currentTopRef.current,
        }
      : undefined;

  return { slotRef, innerRef, tracking, style };
}
